import { Component, EventEmitter, Input, Output, SimpleChanges, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { NgbDate, NgbCalendar, NgbDropdown, NgbDatepicker, NgbDatepickerNavigateEvent } from '@ng-bootstrap/ng-bootstrap';
import { DateFieldService } from '../../../../core/generic/_services/date-field.service';
import { DateService } from '../../../../core/generic/_services/date.service';
import { Subscription } from 'rxjs';
import { DATE_FIELD_OPTIONS } from '../../../../core/common/constants/date-field.constants';
import { DateRange, DateRangePresetId, TimeWindowPreset } from './date-range.model';
import moment from 'moment';

@Component({
  selector: 'kt-date-range',
  templateUrl: './date-range.component.html',
  styleUrls: ['./date-range.component.scss']
})
export class DateRangeComponent implements OnInit, OnDestroy {

  @ViewChild('dr') dropdown?: NgbDropdown;
  @ViewChild('dp') datepicker?: NgbDatepicker;

  @Input() label = '';
  /** Where the label sits relative to the control. Default 'top'; use 'left'/'right' for inline layouts (e.g. toolbars with limited height). */
  @Input() labelPosition: 'top' | 'left' | 'right' | 'bottom' = 'top';
  @Input() dateRange: DateRange | null = null;
  @Input() displayedMonths: number = 2;
  @Input() showSidePanel: boolean = true;
  @Input() calendarWidth: number;
  @Input() calendarBarWidth: number;
  @Input() placeholder: string = null;
  @Input() iconPosition: string = 'right'; //1.left,2.Right
  @Input() defaultDateField: string = (DATE_FIELD_OPTIONS[0]?.id); // Default date field from settings
  @Input() showDateRangeSelector: boolean = false;
  /** Show the preset label "Show by" above the preset options if provided true */
  @Input() showPresetLabel: boolean = false;
  /** When set, only presets whose id is in this array are shown. Ids must be from DateRangePresetId. When empty, falls back to defaultPresets. */
  @Input() customPresets?: DateRangePresetId[] = [];
  @Input() disabled: boolean = false;
  /** ngbDropdown placement. Defaults to 'auto'. Pass e.g. 'top-end bottom-end' to open upward. */
  @Input() placement: string = 'auto';
  /** ngbDropdown container. Pass 'body' to teleport the menu out of overflow-clipping ancestors. */
  @Input() dropdownContainer: 'body' | null = null;
  @Output() onDateRangeChange = new EventEmitter<DateRange>();

  fromDate: NgbDate | null = null;
  toDate: NgbDate | null = null;
  hoveredDate: NgbDate | null = null;
  selectedDateFieldName: string = (DATE_FIELD_OPTIONS[0]?.name); // Display name of selected date field
  presetsToDisplay: TimeWindowPreset[] = [];
  tooltipText: string = '';
  private subscriptions = new Subscription();

  visibleFromMonth: NgbDate | null = null;
  visibleToMonth: NgbDate | null = null;
  yearSelectionSide: 'left' | 'right' | null = null;
  yearOptions: number[] = [];
  monthSelectionSide: 'left' | 'right' | null = null;
  monthOptions: { index: number; name: string }[] = [
    { index: 1, name: 'Jan' },
    { index: 2, name: 'Feb' },
    { index: 3, name: 'Mar' },
    { index: 4, name: 'Apr' },
    { index: 5, name: 'May' },
    { index: 6, name: 'Jun' },
    { index: 7, name: 'Jul' },
    { index: 8, name: 'Aug' },
    { index: 9, name: 'Sep' },
    { index: 10, name: 'Oct' },
    { index: 11, name: 'Nov' },
    { index: 12, name: 'Dec' }
  ];


  /** Master pool of all possible preset options. Display is controlled by customPresets / defaultPresets. */
  timeWindow: TimeWindowPreset[] = [
    { id: 'last1hour', name: 'Last 1 Hour' },
    { id: 'last4hours', name: 'Last 4 Hours' },
    { id: 'last8hours', name: 'Last 8 Hours' },
    { id: 'last12hours', name: 'Last 12 Hours' },
    { id: 'last24hours', name: 'Last 24 Hours' },
    { id: 'today', name: 'Today' },
    { id: 'yesterday', name: 'Yesterday' },
    { id: 'last7days', name: 'Last 7 Days' },
    { id: 'thisWeek', name: 'This Week' },
    { id: 'previousWeek', name: 'Previous Week' },
    { id: 'last15days', name: 'Last 15 Days' },
    { id: 'last30days', name: 'Last 30 Days' },
    { id: 'thisMonth', name: 'This Month' },
    { id: 'previousMonth', name: 'Previous Month' },
    { id: 'last3months', name: 'Last 3 Months' },
    { id: 'last6months', name: 'Last 6 Months' },
    { id: 'last12months', name: 'Last 12 Months' },
    { id: 'thisYear', name: 'This Year' },
    { id: 'previousYear', name: 'Previous Year' },
    { id: 'all', name: 'All' },
  ];
  /** Original short list used when the parent does not pass customPresets. */
  defaultPresets: DateRangePresetId[] = [
    'today', 'last24hours', 'last7days', 'last30days', 'last6months', 'last12months', 'all'
  ];
  currentTimeSelected = this.timeWindow.find(t => t.id === 'last30days');

  constructor(
    private readonly calendar: NgbCalendar,
    private readonly dateFieldService: DateFieldService,
    private readonly dateService: DateService
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    const dateRangeChange = changes?.dateRange;
    if (!dateRangeChange?.currentValue) {
      this.getTooltipText();
      return;
    }
    let dateRange = dateRangeChange.currentValue;
    if (dateRange.id && dateRange.id != 'custom') {
      this.currentTimeSelected = this.timeWindow.find(t => t.id === dateRange.id)
    }
    else {
      this.currentTimeSelected = null;
      if (dateRange.from || dateRange.to) {
        this.fromDate = this.toNgbDate(new Date(dateRange.from)),
          this.toDate = this.toNgbDate(new Date(dateRange.to))
      }
    }
    this.getTooltipText();
  }

  ngOnInit(): void {
    if (this.showSidePanel) {
      const presetsToUse = this.customPresets?.length ? this.customPresets : this.defaultPresets;
      this.presetsToDisplay = presetsToUse
        .map((id) => this.timeWindow.find((t) => t.id === id))
        .filter((t): t is TimeWindowPreset => !!t);
    }

    if (this.placeholder) {
      this.currentTimeSelected = {
        id: null,
        name: this.placeholder
      }
    }
    if(this.currentTimeSelected) {
      this.selectTimeWindow(this.currentTimeSelected, false);
    }

    // Initialize date field display name using persisted selection (fallback to default)
    const storedDateField = this.dateFieldService.getSelectedDateField();
    this.updateDateFieldName(storedDateField || this.defaultDateField);

    // Subscribe to date field changes to update tooltip
    this.subscriptions.add(
      this.dateFieldService.dateFieldSelectionChanged.subscribe((fieldId: string) => {
        this.updateDateFieldName(fieldId);
        this.getTooltipText();
      })
    );
    this.getTooltipText();

    const today = this.calendar.getToday();
    this._updateVisibleMonths(today.year, today.month);
    this._initYearOptions(today.year);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private updateDateFieldName(fieldId: string): void {
    const field = DATE_FIELD_OPTIONS.find(f => f.id === fieldId);
    this.selectedDateFieldName = field ? field.name : (DATE_FIELD_OPTIONS[0]?.name);
  }

  getTooltipText(): void {
    const prefix = this.showDateRangeSelector ? this.selectedDateFieldName + ': ' : '';
    if (this.currentTimeSelected) {
      this.tooltipText = prefix + this.currentTimeSelected.name;
    } else {
      const fromStr = this.fromDate ? moment(this.toJSDate(this.fromDate)).format('MMM DD YYYY') : '';
      const toStr = this.toDate ? moment(this.toJSDate(this.toDate)).format('MMM DD YYYY') : '';
      this.tooltipText = prefix + fromStr + ' - ' + toStr;
    }
  }

  onCalendarNavigate(event: NgbDatepickerNavigateEvent): void {
    const next = event.next;
    if (!next) {
      return;
    }
    this._updateVisibleMonths(next.year, next.month);
    this._initYearOptions(next.year);
  }

  private _updateVisibleMonths(year: number, month: number): void {
    this.visibleFromMonth = new NgbDate(year, month, 1);
    let endMonth = month + (this.displayedMonths - 1);
    let endYear = year;
    while (endMonth > 12) {
      endMonth -= 12;
      endYear += 1;
    }
    this.visibleToMonth = new NgbDate(endYear, endMonth, 1);
  }

  private _initYearOptions(centerYear: number): void {
    const startYear = centerYear - 12;
    const endYear = startYear + 39;
    const years: number[] = [];
    for (let y = startYear; y <= endYear; y++) {
      years.push(y);
    }
    this.yearOptions = years;
  }

  toJSDate(ngbDate: NgbDate | null): Date | null {
    if (!ngbDate) return null;
    return new Date(ngbDate.year, ngbDate.month - 1, ngbDate.day);
  }

  toNgbDate(date: Date | null): NgbDate | null {
    if (!date) return null;
    return new NgbDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
  }

  selectTimeWindow(e, isEmitterCalled = true) {
    this.currentTimeSelected = e;
    this.fromDate = null;
    this.toDate = null;

    let range: any = this.dateService.getDatesForPreset(e.id);

    if (isEmitterCalled) {
      this.onDateRangeChange.emit(range);
    }
    this.dropdown?.close();
  }

  toggleYearSelection(side: 'left' | 'right'): void {
    this.monthSelectionSide = null;
    this.yearSelectionSide = this.yearSelectionSide === side ? null : side;
  }

  selectYear(year: number): void {
    if (!this.visibleFromMonth) {
      const today = this.calendar.getToday();
      this._updateVisibleMonths(today.year, today.month);
    }
    const isRight = this.yearSelectionSide === 'right';
    const baseMonth = isRight ? this.visibleToMonth : this.visibleFromMonth;
    let month = baseMonth ? baseMonth.month : this.calendar.getToday().month;
    if (isRight) {
      month -= (this.displayedMonths - 1);
      if (month < 1) {
        month += 12;
        year -= 1;
      }
    }
    if (this.datepicker) {
      this.datepicker.navigateTo({ year, month });
    }
    this.yearSelectionSide = null;
  }

  toggleMonthSelection(side: 'left' | 'right'): void {
    this.yearSelectionSide = null;
    this.monthSelectionSide = this.monthSelectionSide === side ? null : side;
  }

  selectMonth(month: number): void {
    if (!this.visibleFromMonth) {
      const today = this.calendar.getToday();
      this._updateVisibleMonths(today.year, today.month);
    }
    const isRight = this.monthSelectionSide === 'right';
    const baseMonth = isRight ? this.visibleToMonth : this.visibleFromMonth;
    let year = baseMonth ? baseMonth.year : this.calendar.getToday().year;
    let navigateMonth = month;
    if (isRight) {
      navigateMonth -= (this.displayedMonths - 1);
      while (navigateMonth < 1) {
        navigateMonth += 12;
        year -= 1;
      }
    }
    if (this.datepicker) {
      this.datepicker.navigateTo({ year, month: navigateMonth });
    }
    this.monthSelectionSide = null;
  }

  private getMonthNameParts(monthHeader: HTMLElement): { monthText: string; yearText: string } | null {
    const label = (monthHeader.textContent || '').trim().replace(/\s+/g, ' ');
    const parts = label.split(' ');
    if (parts.length < 2) {
      return null;
    }
    return {
      monthText: parts.slice(0, -1).join(' '),
      yearText: parts[parts.length - 1]
    };
  }

  private getClickTargetType(event: MouseEvent, monthHeader: HTMLElement): 'month' | 'year' {
    const parts = this.getMonthNameParts(monthHeader);
    if (!parts) {
      return 'year';
    }

    const computedStyle = window.getComputedStyle(monthHeader);
    const font = [
      computedStyle.fontStyle,
      computedStyle.fontVariant,
      computedStyle.fontWeight,
      computedStyle.fontSize,
      computedStyle.fontFamily
    ].join(' ');
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) {
      return 'year';
    }

    context.font = font;
    const monthWidth = context.measureText(parts.monthText).width;
    const totalWidth = context.measureText(`${parts.monthText} ${parts.yearText}`).width;
    const headerRect = monthHeader.getBoundingClientRect();
    const contentStart = headerRect.left + ((monthHeader.clientWidth - totalWidth) / 2);
    const clickX = event.clientX;

    return clickX <= contentStart + monthWidth ? 'month' : 'year';
  }

  handleHostClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }
    const monthHeader = target.classList.contains('ngb-dp-month-name')
      ? target
      : target.closest('.ngb-dp-month-name') as HTMLElement | null;
    if (monthHeader) {
      const nav = monthHeader.closest('ngb-datepicker-navigation');
      const clickedType = this.getClickTargetType(event, monthHeader);
      if (nav) {
        const allNames = Array.from(nav.querySelectorAll('.ngb-dp-month-name'));
        const index = allNames.indexOf(monthHeader);
        const side = index <= 0 ? 'left' : 'right';
        if (clickedType === 'month') {
          this.toggleMonthSelection(side);
        } else {
          this.toggleYearSelection(side);
        }
      } else {
        if (clickedType === 'month') {
          this.toggleMonthSelection('left');
        } else {
          this.toggleYearSelection('left');
        }
      }
    }
  }

  onDateSelection(date: NgbDate) {
    if (!this.fromDate && !this.toDate) {
      this.fromDate = date;
    } else if (this.fromDate && !this.toDate && date.after(this.fromDate)) {
      this.toDate = date;
      this.currentTimeSelected = null;
      this.dropdown?.close();
    } else {
      this.toDate = null;
      this.fromDate = date;
    }
    if (this.fromDate && this.toDate) {
      this.onDateRangeChange.emit({ from: this.toJSDate(this.fromDate) as Date, to: this.toJSDate(this.toDate) as Date, id: 'custom' });
    }
  }

  isInside(date: NgbDate) {
    return this.toDate && date.after(this.fromDate) && date.before(this.toDate);
  }

  isRange(date: NgbDate) {
    return date.equals(this.fromDate) || (this.toDate && date.equals(this.toDate));
  }
}
